/**
 * The strip that says, on every page, that this is not the admin's own view.
 *
 * Loud on purpose: solid amber, pinned with the top bar, never dismissable.
 * An admin who forgets they are looking through an editor's eyes will read an
 * empty board as a bug, or worse, act on it — and every write they make while
 * here is real. The way out sits in the strip itself, so leaving never
 * depends on finding the menu that started it.
 */
import { EyeIcon, LogOutIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useSession, useStopViewAs } from "@/hooks/use-session";
import { guardLeave } from "@/features/save-bar/save-bar-store";

export function ViewAsBanner() {
  const { t } = useTranslation();
  const { viewAs } = useSession();
  const stop = useStopViewAs();
  const { pathname } = useLocation();
  if (!viewAs) return null;

  // The Studio refuses every write under view-as (the agents' chats and jobs are
  // people's work in progress), so there the note says so rather than "changes are real".
  const studio = pathname === "/estudio" || pathname.startsWith("/estudio/") || pathname.startsWith("/admin/agentes");

  const role = t(`viewAs.roleInline.${viewAs.role}`);
  const message = viewAs.label
    ? t("viewAs.bannerNamed", { name: viewAs.label, role })
    : t("viewAs.bannerRole", { role });

  return (
    <div
      role="status"
      className="bg-warning text-warning-foreground flex min-h-11 items-center gap-2 px-3 py-1.5 text-sm sm:gap-3 sm:px-4"
    >
      {viewAs.editor ? (
        <span
          aria-hidden
          className="ring-warning-foreground/30 size-3 shrink-0 rounded-full ring-2"
          style={{ backgroundColor: viewAs.editor.color }}
        />
      ) : (
        <EyeIcon aria-hidden className="size-4 shrink-0" />
      )}
      <p className="min-w-0 flex-1 leading-snug">
        <span className="font-semibold">{message}</span>
        <span className="hidden opacity-80 md:inline"> · {t(studio ? "viewAs.bannerNoteStudio" : "viewAs.bannerNote")}</span>
      </p>
      <Button
        size="sm"
        onClick={() => guardLeave(() => stop.mutate())}
        disabled={stop.isPending}
        className="bg-warning-foreground text-warning hover:bg-warning-foreground/90 shrink-0"
      >
        {stop.isPending ? <Spinner data-icon="inline-start" /> : <LogOutIcon data-icon="inline-start" />}
        {t("viewAs.exit")}
      </Button>
    </div>
  );
}
